import React, { useState, useEffect } from 'react';
import type { BusinessCategory, ContentType } from '@/types/business';
import './ListingSubmissionModal.css';

interface ListingSubmissionModalProps {
  onClose: () => void;
}

type Step = 'type' | 'details' | 'contact' | 'confirm';
type CoverageType = 'radius' | 'region' | 'national' | 'international';

interface FormData {
  contentType: ContentType | '';
  name: string;
  tagline: string;
  description: string;
  category: BusinessCategory | '';
  // Coverage — business only
  coverageType: CoverageType | '';
  coverageTown: string;
  coverageRadiusMiles: string;
  geographicArea: string;   // Used when coverageType === 'region'
  country: string;
  postcode: string;
  website: string;
  socialHandle: string;
  youtubeChannel: string;
  organicCertified: boolean;
  contactName: string;
  contactEmail: string;
  agreeTerms: boolean;
}

const CONTENT_TYPES: { value: ContentType; emoji: string; label: string; desc: string }[] = [
  { value: 'business', emoji: '🏪', label: 'Business', desc: 'Nursery, shop, supplier, designer, consultant' },
  { value: 'blog',     emoji: '✍️', label: 'Blog', desc: 'Written content about growing and gardening' },
  { value: 'vlog',     emoji: '📹', label: 'Vlog', desc: 'YouTube channel or video content about gardening' },
  { value: 'podcast',  emoji: '🎙', label: 'Podcast', desc: 'Audio show about gardening or horticulture' },
];

const CATEGORIES: { value: BusinessCategory; label: string }[] = [
  { value: 'nursery',          label: '🌿 Nursery' },
  { value: 'seed-supplier',    label: '🌱 Seed supplier' },
  { value: 'organic-supplies', label: '🧴 Organic supplies' },
  { value: 'garden-design',    label: '🏡 Garden design' },
  { value: 'landscaping',      label: '🪨 Landscaping' },
  { value: 'market-garden',    label: '🥦 Market garden' },
  { value: 'consultancy',      label: '🧑‍🌾 Consultancy' },
  { value: 'gardening-school', label: '📚 Gardening school' },
  { value: 'composting',       label: '♻️ Composting' },
  { value: 'beekeeping',       label: '🐝 Beekeeping' },
  { value: 'tool-hire',        label: '🔧 Tool hire' },
  { value: 'other',            label: '🌱 Other' },
];

const COVERAGE_OPTIONS: { value: CoverageType; label: string; desc: string }[] = [
  { value: 'radius',        label: '📍 Local area',     desc: 'Customers within a radius of a town' },
  { value: 'region',        label: '🗺️ Region / county', desc: 'A specific area, county, or region' },
  { value: 'national',      label: '🏴 Whole country',   desc: 'National delivery or mail order' },
  { value: 'international', label: '🌍 International',   desc: 'Multiple countries or worldwide' },
];

const RADIUS_OPTIONS = ['5', '10', '15', '25', '50', '100'];

const COUNTRIES = [
  'United Kingdom', 'Ireland', 'Australia', 'New Zealand',
  'United States', 'Canada', 'South Africa', 'Other',
];

const STEPS: { key: Step; label: string }[] = [
  { key: 'type',    label: 'Type' },
  { key: 'details', label: 'Details' },
  { key: 'contact', label: 'Contact' },
  { key: 'confirm', label: 'Review' },
];

const EMPTY: FormData = {
  contentType: '', name: '', tagline: '', description: '', category: '',
  coverageType: '', coverageTown: '', coverageRadiusMiles: '10',
  geographicArea: '', country: 'United Kingdom', postcode: '',
  website: '', socialHandle: '', youtubeChannel: '',
  organicCertified: false, contactName: '', contactEmail: '', agreeTerms: false,
};

function buildGeographicArea(form: FormData): string {
  if (form.contentType !== 'business') return 'Worldwide';
  switch (form.coverageType) {
    case 'radius':        return `Within ${form.coverageRadiusMiles} miles of ${form.coverageTown}`;
    case 'region':        return form.geographicArea;
    case 'national':      return form.country;
    case 'international': return 'International / Worldwide';
    default:              return form.geographicArea || form.country;
  }
}

export default function ListingSubmissionModal({ onClose }: ListingSubmissionModalProps) {
  const [step, setStep] = useState<Step>('type');
  const [form, setForm] = useState<FormData>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const stepIndex = STEPS.findIndex(s => s.key === step);

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(e => ({ ...e, [key]: undefined }));
  }

  function validateStep(): boolean {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (step === 'type' && !form.contentType) errs.contentType = 'Please select a type';
    if (step === 'details') {
      if (!form.name.trim()) errs.name = 'Name is required';
      if (!form.tagline.trim()) errs.tagline = 'A short tagline is required';
      if (form.description.trim().length < 20) errs.description = 'Please write at least 20 characters';
      if (form.contentType === 'business') {
        if (!form.category) errs.category = 'Please choose a category';
        if (!form.coverageType) errs.coverageType = 'Please select your coverage area';
        if (form.coverageType === 'radius' && !form.coverageTown.trim()) errs.coverageTown = 'Please enter your town or nearest town';
        if (form.coverageType === 'region' && !form.geographicArea.trim()) errs.geographicArea = 'Please describe your area or region';
      }
    }
    if (step === 'contact') {
      if (!form.contactName.trim()) errs.contactName = 'Your name is required';
      if (!form.contactEmail.trim() || !form.contactEmail.includes('@')) errs.contactEmail = 'A valid email is required';
    }
    if (step === 'confirm' && !form.agreeTerms) errs.agreeTerms = 'Please agree to the terms';
    setErrors(errs);
    const hasErrors = Object.keys(errs).length > 0;
    if (hasErrors) {
      // Scroll the modal body to the first error
      setTimeout(() => {
        const firstErr = document.querySelector('.field-error');
        firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
    return !hasErrors;
  }

  function next() {
    if (!validateStep()) return;
    const order: Step[] = ['type', 'details', 'contact', 'confirm'];
    const idx = order.indexOf(step);
    if (idx < order.length - 1) setStep(order[idx + 1]);
  }

  function back() {
    const order: Step[] = ['type', 'details', 'contact', 'confirm'];
    const idx = order.indexOf(step);
    if (idx > 0) setStep(order[idx - 1]);
  }

  async function submit() {
    if (!validateStep() || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        description: form.description,
        contentType: form.contentType,
        categories: form.category ? [form.category] : [],
        geographicArea: buildGeographicArea(form),
        country: form.country || 'United Kingdom',
        postcode: form.postcode || undefined,
        coverageType: form.contentType === 'business' ? (form.coverageType || 'region') : 'international',
        coverageTown: form.coverageType === 'radius' ? form.coverageTown : undefined,
        coverageRadiusMiles: form.coverageType === 'radius' ? parseInt(form.coverageRadiusMiles) : undefined,
        website: form.website || undefined,
        socialLinks: {
          instagram: form.socialHandle ? form.socialHandle.replace('@', '') : undefined,
          youtube: form.youtubeChannel || undefined,
        },
        organicCertified: form.contentType === 'business' ? form.organicCertified : false,
        services: [],
        contactName: form.contactName,
        contactEmail: form.contactEmail,
      };

      const res = await fetch('/api/businesses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="lsm-overlay" role="dialog" aria-modal="true" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="lsm-modal paper lsm-modal--success">
          <div className="lsm-success">
            <span className="lsm-success-icon">🌿</span>
            <h2 className="text-headline">Thank you!</h2>
            <p className="text-body" style={{ color: 'var(--ink-light)', textAlign: 'center' }}>
              Your listing for <strong>{form.name}</strong> has been received. We'll review it within 2–3 working days and get in touch at {form.contactEmail}.
            </p>
            <button className="btn-sage" onClick={onClose} style={{ marginTop: 'var(--space-4)' }}>
              Back to directory
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedType = CONTENT_TYPES.find(t => t.value === form.contentType);
  const coverageSummary = form.contentType ? buildGeographicArea(form) : '';

  return (
    <div className="lsm-overlay" role="dialog" aria-modal="true" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lsm-modal paper">

        {/* Header */}
        <div className="lsm-header">
          <div className="lsm-header-left">
            {stepIndex > 0 && (
              <button className="lsm-back" onClick={back} aria-label="Back">←</button>
            )}
            <div>
              <h2 className="text-title lsm-title">List your {form.contentType || 'business'}</h2>
              <p className="text-caption" style={{ color: 'var(--ink-faint)' }}>
                Step {stepIndex + 1} of {STEPS.length} — {STEPS[stepIndex].label}
              </p>
            </div>
          </div>
          <button className="lsm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Progress */}
        <div className="lsm-progress">
          <div className="lsm-progress-bar" style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }} />
        </div>

        {/* Step indicators */}
        <div className="lsm-steps">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`lsm-step ${i === stepIndex ? 'lsm-step--active' : ''} ${i < stepIndex ? 'lsm-step--done' : ''}`}>
              <span className="lsm-step-num">{i < stepIndex ? '✓' : i + 1}</span>
              <span className="lsm-step-label text-caption">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="lsm-body">

          {/* ---- TYPE ---- */}
          {step === 'type' && (
            <div className="lsm-step-content">
              <h3 className="text-title lsm-step-title">What are you listing?</h3>
              <p className="text-body lsm-step-desc">Choose the type that best describes your business, blog, or channel.</p>
              <div className="lsm-type-grid">
                {CONTENT_TYPES.map(t => (
                  <button
                    key={t.value}
                    className={`lsm-type-btn ${form.contentType === t.value ? 'lsm-type-btn--active' : ''}`}
                    onClick={() => set('contentType', t.value)}
                  >
                    <span className="lsm-type-emoji">{t.emoji}</span>
                    <span className="text-hand lsm-type-label">{t.label}</span>
                    <span className="text-caption lsm-type-desc">{t.desc}</span>
                  </button>
                ))}
              </div>
              {errors.contentType && <p className="field-error">{errors.contentType}</p>}
            </div>
          )}

          {/* ---- DETAILS ---- */}
          {step === 'details' && (
            <div className="lsm-step-content">
              <h3 className="text-title lsm-step-title">
                {selectedType?.emoji} About your {form.contentType}
              </h3>

              {form.contentType === 'business' && (
                <div className="field">
                  <label>Category <span className="field-hint-inline">required</span></label>
                  <div className="lsm-cat-grid">
                    {CATEGORIES.map(c => (
                      <button
                        key={c.value}
                        className={`lsm-cat-btn ${form.category === c.value ? 'lsm-cat-btn--active' : ''}`}
                        onClick={() => set('category', c.value)}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  {errors.category && <p className="field-error">{errors.category}</p>}
                </div>
              )}

              <div className="field">
                <label>
                  {form.contentType === 'business' ? 'Business name' : form.contentType === 'blog' ? 'Blog name' : form.contentType === 'vlog' ? 'Channel name' : 'Podcast name'}
                  <span className="field-hint-inline"> required</span>
                </label>
                <input
                  className={`input ${errors.name ? 'input--error' : ''}`}
                  type="text"
                  placeholder="e.g. Green Fingers Nursery"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  maxLength={100}
                />
                {errors.name && <p className="field-error">{errors.name}</p>}
              </div>

              <div className="field">
                <label>Tagline <span className="field-hint-inline">required — max 100 chars</span></label>
                <input
                  className={`input ${errors.tagline ? 'input--error' : ''}`}
                  type="text"
                  placeholder="e.g. Rare heritage vegetables and soft fruit since 1987"
                  value={form.tagline}
                  onChange={e => set('tagline', e.target.value)}
                  maxLength={100}
                />
                {errors.tagline && <p className="field-error">{errors.tagline}</p>}
              </div>

              <div className="field">
                <label>Description <span className="field-hint-inline">min 20 chars</span></label>
                <textarea
                  className={`textarea ${errors.description ? 'input--error' : ''}`}
                  rows={5}
                  placeholder="Tell growers about your business, what makes you special, and what you offer…"
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                />
                <span className="field-hint">{form.description.length} / 1000 characters</span>
                {errors.description && <p className="field-error" id="err-description">{errors.description}</p>}
              </div>

              {/* ── Coverage section — businesses only ── */}
              {form.contentType === 'business' && (
                <div className="field lsm-coverage-section">
                  <label>Where do you serve customers? <span className="field-hint-inline">required</span></label>
                  <div className="lsm-coverage-grid">
                    {COVERAGE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        className={`lsm-coverage-btn ${form.coverageType === opt.value ? 'lsm-coverage-btn--active' : ''}`}
                        onClick={() => set('coverageType', opt.value)}
                      >
                        <span className="lsm-coverage-label">{opt.label}</span>
                        <span className="lsm-coverage-desc text-caption">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                  {errors.coverageType && <p className="field-error">{errors.coverageType}</p>}

                  {/* Radius fields */}
                  {form.coverageType === 'radius' && (
                    <div className="lsm-row lsm-coverage-detail">
                      <div className="field">
                        <label>Nearest town <span className="field-hint-inline">required</span></label>
                        <input
                          className={`input ${errors.coverageTown ? 'input--error' : ''}`}
                          type="text"
                          placeholder="e.g. Exeter"
                          value={form.coverageTown}
                          onChange={e => set('coverageTown', e.target.value)}
                        />
                        {errors.coverageTown && <p className="field-error">{errors.coverageTown}</p>}
                      </div>
                      <div className="field">
                        <label>Radius</label>
                        <select
                          className="input"
                          value={form.coverageRadiusMiles}
                          onChange={e => set('coverageRadiusMiles', e.target.value)}
                        >
                          {RADIUS_OPTIONS.map(r => (
                            <option key={r} value={r}>{r} miles</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Region field */}
                  {form.coverageType === 'region' && (
                    <div className="field lsm-coverage-detail">
                      <label>Area / region <span className="field-hint-inline">required</span></label>
                      <input
                        className={`input ${errors.geographicArea ? 'input--error' : ''}`}
                        type="text"
                        placeholder="e.g. Devon, South West England"
                        value={form.geographicArea}
                        onChange={e => set('geographicArea', e.target.value)}
                      />
                      {errors.geographicArea && <p className="field-error">{errors.geographicArea}</p>}
                    </div>
                  )}

                  {/* Country field for national / international */}
                  {(form.coverageType === 'national' || form.coverageType === 'international') && (
                    <div className="field lsm-coverage-detail">
                      <label>
                        {form.coverageType === 'national' ? 'Country' : 'Primary country'}
                        <span className="field-hint-inline"> required</span>
                      </label>
                      <select
                        className="input"
                        value={form.country}
                        onChange={e => set('country', e.target.value)}
                      >
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {form.coverageType === 'international' && (
                        <p className="field-hint">International / mail order businesses are shown to all gardeners regardless of their country.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Country for non-business types */}
              {form.contentType !== 'business' && (
                <div className="field">
                  <label>Country</label>
                  <select className="input" value={form.country} onChange={e => set('country', e.target.value)}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <span className="field-hint">Blogs, vlogs and podcasts are shown worldwide.</span>
                </div>
              )}

              <div className="lsm-row">
                <div className="field">
                  <label>Postcode <span className="field-hint-inline">optional</span></label>
                  <input
                    className="input"
                    type="text"
                    placeholder="e.g. EX1 1AA"
                    value={form.postcode}
                    onChange={e => set('postcode', e.target.value)}
                    maxLength={8}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="field">
                  <label>Website</label>
                  <input
                    className="input"
                    type="url"
                    placeholder="https://yoursite.com"
                    value={form.website}
                    onChange={e => set('website', e.target.value)}
                  />
                </div>
              </div>

              <div className="field">
                <label>
                  {form.contentType === 'vlog' ? 'YouTube channel URL' : 'Instagram / social handle'}
                </label>
                {form.contentType === 'vlog' ? (
                  <input
                    className="input"
                    type="url"
                    placeholder="https://youtube.com/@yourchannel"
                    value={form.youtubeChannel}
                    onChange={e => set('youtubeChannel', e.target.value)}
                  />
                ) : (
                  <input
                    className="input"
                    type="text"
                    placeholder="@yourhandle"
                    value={form.socialHandle}
                    onChange={e => set('socialHandle', e.target.value)}
                  />
                )}
              </div>

              {form.contentType === 'business' && (
                <label className="lsm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.organicCertified}
                    onChange={e => set('organicCertified', e.target.checked)}
                  />
                  <span>🌿 We are organically certified (Soil Association / OF&G)</span>
                </label>
              )}
            </div>
          )}

          {/* ---- CONTACT ---- */}
          {step === 'contact' && (
            <div className="lsm-step-content">
              <h3 className="text-title lsm-step-title">Your contact details</h3>
              <p className="text-body lsm-step-desc">
                We'll use these to review your listing and get in touch. They won't be published.
              </p>

              <div className="field">
                <label>Your name <span className="field-hint-inline">required</span></label>
                <input
                  className={`input ${errors.contactName ? 'input--error' : ''}`}
                  type="text"
                  placeholder="Jane Smith"
                  value={form.contactName}
                  onChange={e => set('contactName', e.target.value)}
                />
                {errors.contactName && <p className="field-error">{errors.contactName}</p>}
              </div>

              <div className="field">
                <label>Email address <span className="field-hint-inline">required</span></label>
                <input
                  className={`input ${errors.contactEmail ? 'input--error' : ''}`}
                  type="email"
                  placeholder="jane@yoursite.com"
                  value={form.contactEmail}
                  onChange={e => set('contactEmail', e.target.value)}
                />
                {errors.contactEmail && <p className="field-error">{errors.contactEmail}</p>}
              </div>

              <div className="lsm-info-box">
                <span>📋</span>
                <div>
                  <strong>What happens next?</strong>
                  <p className="text-small" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-1)' }}>
                    We'll review your submission within 2–3 working days. Basic listings are free. Featured placement is available for a small annual fee. You'll hear from us at the email above.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ---- CONFIRM ---- */}
          {step === 'confirm' && (
            <div className="lsm-step-content">
              <h3 className="text-title lsm-step-title">Review your listing</h3>

              <div className="lsm-review paper">
                <div className="lsm-review-header">
                  <span className="lsm-review-type-badge">
                    {selectedType?.emoji} {form.contentType}
                    {form.category && ` · ${CATEGORIES.find(c => c.value === form.category)?.label}`}
                  </span>
                  {form.organicCertified && <span className="text-caption" style={{ color: 'var(--sage)' }}>🌿 Organic certified</span>}
                </div>
                <h4 className="text-hand" style={{ fontSize: '1.2rem', color: 'var(--ink)' }}>{form.name}</h4>
                <p className="text-hand" style={{ color: 'var(--sage)', fontStyle: 'italic' }}>{form.tagline}</p>
                <p className="text-small" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-3)' }}>
                  {form.description.slice(0, 200)}{form.description.length > 200 ? '…' : ''}
                </p>
                <div className="lsm-review-meta">
                  {coverageSummary && <span className="text-caption">📍 {coverageSummary}</span>}
                  {form.website && <span className="text-caption">🔗 {form.website}</span>}
                  {form.socialHandle && <span className="text-caption">📸 {form.socialHandle}</span>}
                  {form.youtubeChannel && <span className="text-caption">📹 {form.youtubeChannel}</span>}
                </div>
                <div className="lsm-review-contact">
                  <span className="text-caption" style={{ color: 'var(--ink-faint)' }}>Contact: {form.contactName} · {form.contactEmail}</span>
                </div>
              </div>

              {submitError && (
                <p className="field-error" style={{ marginTop: 'var(--space-3)' }}>{submitError}</p>
              )}

              <label className="lsm-checkbox-label" style={{ marginTop: 'var(--space-4)' }}>
                <input
                  type="checkbox"
                  checked={form.agreeTerms}
                  onChange={e => set('agreeTerms', e.target.checked)}
                />
                <span className="text-small">
                  I confirm this information is accurate and agree to the{' '}
                  <a href="/terms" style={{ color: 'var(--sage)' }} onClick={e => e.stopPropagation()}>listing terms</a>.
                  Basic listings are free. We may contact you about featured placement.
                </span>
              </label>
              {errors.agreeTerms && <p className="field-error">{errors.agreeTerms}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="lsm-footer">
          {stepIndex > 0 ? (
            <button className="gsf-nav-btn" onClick={back}>← Back</button>
          ) : (
            <button className="gsf-nav-btn" onClick={onClose}>Cancel</button>
          )}

          {step !== 'confirm' ? (
            <button className="btn-sage" onClick={next}>Continue →</button>
          ) : (
            <button className="btn-sage" onClick={submit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting…' : 'Submit listing'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
